import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, MapPin, Users, Building, Check, ArrowRight, Loader, ArrowLeft, FileText, Upload } from 'lucide-react';

export default function OnboardingPage({ user, API_URL }) {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        siret: '',
        kbis_url: '',
        activity_sector: '',
        has_premises: null,
        team_size: '',
        department: ''
    });

    const sectors = [
        { id: 'restauration', label: 'Restauration / Hôtellerie', icon: '🍽️' },
        { id: 'btp', label: 'BTP / Artisanat', icon: '🛠️' },
        { id: 'commerce', label: 'Commerce de détail', icon: '🛍️' },
        { id: 'services', label: 'Services / Bureau', icon: '💻' },
        { id: 'sante', label: 'Santé / Soins', icon: '⚕️' },
        { id: 'transport', label: 'Transport / Logistique', icon: '🚚' },
        { id: 'autre', label: 'Autre activité', icon: '✨' }
    ];

    const teamSizes = [
        { id: '0', label: 'Je suis seul (Auto-entrepreneur)' },
        { id: '1-2', label: '1 à 2 salariés' },
        { id: '3-10', label: '3 à 10 salariés' },
        { id: '11+', label: '11 salariés et plus' }
    ];

    const updateField = (field, value, nextStep = true) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(null);

        if (nextStep && step < 5) {
            setTimeout(() => setStep(step + 1), 150);
        }
    };

    const handleSubmit = async () => {
        if (!formData.department) {
            setError("Veuillez renseigner votre département.");
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/user/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error("Erreur de sauvegarde du profil.");

            navigate('/dashboard');
        } catch (error) {
            setError("Erreur lors de la sauvegarde du profil : " + error.message);
        }
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
            <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col border border-slate-200">

                {/* Header Progress */}
                <div className="bg-slate-900 p-8 text-white">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-2xl font-bold">Bienvenue chez {user?.company_name || 'Reglo'} !</h1>
                            <p className="text-slate-400 text-sm mt-1">Configurons le profil pour <span className="text-emerald-400 font-semibold">{user?.company_name || 'votre entreprise'}</span>.</p>
                        </div>
                        <div className="text-right">
                            <span className="text-4xl font-bold text-emerald-400">{step}</span>
                            <span className="text-slate-500">/5</span>
                        </div>
                    </div>

                    {/* Barre de progression */}
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-emerald-500 transition-all duration-500 ease-out"
                            style={{ width: `${(step / 5) * 100}%` }}
                        />
                    </div>
                </div>

                <div className="p-8 flex-grow">
                    {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm border border-red-200">{error}</div>}

                    {/* QUESTION 1 (NEW) : ADMINISTRATIVE */}
                    {/* QUESTION 1 (NEW) : ADMINISTRATIVE */}
                    {step === 1 && (
                        <div className="animate-in slide-in-from-right duration-300">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                <FileText className="w-6 h-6 text-emerald-600" /> Identité administrative de {user?.company_name}
                            </h2>

                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 text-sm text-blue-800">
                                <p className="font-semibold mb-1">Pourquoi remplir ces informations ?</p>
                                <p>Ces données nous permettent de pré-remplir vos documents et d'adapter votre veille règlementaire à votre convention collective.</p>
                            </div>

                            <label className="block text-slate-700 font-medium mb-2">Numéro SIRET (Recommandé)</label>
                            <input
                                type="text"
                                placeholder="14 chiffres"
                                className="w-full p-4 text-lg border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-0 outline-none mb-6 transition-colors"
                                value={formData.siret}
                                onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                                autoFocus
                            />

                            <label className="block text-slate-700 font-medium mb-2">Lien vers Kbis</label>
                            <input
                                type="text"
                                placeholder="https://..."
                                className="w-full p-4 text-lg border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-0 outline-none mb-8 transition-colors"
                                value={formData.kbis_url}
                                onChange={(e) => setFormData({ ...formData, kbis_url: e.target.value })}
                            />

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => setStep(2)}
                                    className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-lg hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                                >
                                    Valider et continuer <ArrowRight className="w-5 h-5" />
                                </button>

                                <button
                                    onClick={() => setStep(2)}
                                    className="w-full py-2 text-slate-400 hover:text-slate-600 font-medium text-sm transition-colors"
                                >
                                    Je le ferai plus tard
                                </button>
                            </div>
                        </div>
                    )}

                    {/* QUESTION 2 : ACTIVITÉ */}
                    {step === 2 && (
                        <div className="animate-in slide-in-from-right duration-300">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                <Briefcase className="w-6 h-6 text-emerald-600" /> Quelle est l'activité principale de {user?.company_name} ?
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {sectors.map(s => (
                                    <button
                                        key={s.id}
                                        onClick={() => updateField('activity_sector', s.label)}
                                        className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 hover:shadow-md ${formData.activity_sector === s.label
                                            ? 'border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600'
                                            : 'border-slate-100 hover:border-emerald-400 hover:bg-white'
                                            }`}
                                    >
                                        <span className="text-2xl">{s.icon}</span>
                                        <span className="font-medium text-slate-700">{s.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* QUESTION 3 : LOCAUX */}
                    {step === 3 && (
                        <div className="animate-in slide-in-from-right duration-300">
                            <h2 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                                <Building className="w-6 h-6 text-emerald-600" /> Avez-vous un local professionnel ?
                            </h2>
                            <p className="text-slate-500 mb-8 text-sm ml-9">Détermine les obligations ERP de {user?.company_name || 'votre entreprise'}.</p>

                            <div className="flex flex-col gap-4">
                                <button onClick={() => updateField('has_premises', true)} className="p-6 rounded-xl border-2 border-slate-100 hover:border-emerald-500 hover:shadow-md text-left flex items-center justify-between group transition-all">
                                    <span className="font-bold text-lg text-slate-700 group-hover:text-emerald-800">Oui (Bureau, Commerce, etc.)</span>
                                    <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-600" />
                                </button>
                                <button onClick={() => updateField('has_premises', false)} className="p-6 rounded-xl border-2 border-slate-100 hover:border-emerald-500 hover:shadow-md text-left flex items-center justify-between group transition-all">
                                    <span className="font-bold text-lg text-slate-700 group-hover:text-emerald-800">Non (Domicile ou chez le client)</span>
                                    <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-600" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* QUESTION 4 : ÉQUIPE */}
                    {step === 4 && (
                        <div className="animate-in slide-in-from-right duration-300">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                <Users className="w-6 h-6 text-emerald-600" /> Quelle est la taille de l'équipe ?
                            </h2>
                            <div className="grid grid-cols-1 gap-3">
                                {teamSizes.map(t => (
                                    <button
                                        key={t.id}
                                        onClick={() => updateField('team_size', t.label)}
                                        className="p-5 rounded-xl border-2 border-slate-100 hover:border-emerald-500 hover:shadow-md text-left font-medium text-slate-700 transition-all"
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* QUESTION 5 : LOCALISATION */}
                    {step === 5 && (
                        <div className="animate-in slide-in-from-right duration-300">
                            <h2 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                                <MapPin className="w-6 h-6 text-emerald-600" /> Où est situé {user?.company_name || 'l\'établissement'} ?
                            </h2>
                            <p className="text-slate-500 mb-6 text-sm ml-9">Pour les règles spécifiques (jours fériés locaux, aides...).</p>

                            <input
                                type="text"
                                placeholder="Département (Ex: 75 ou Bas-Rhin)"
                                className="w-full p-4 text-lg border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-0 outline-none mb-8 transition-colors"
                                value={formData.department}
                                onChange={(e) => updateField('department', e.target.value, false)}
                                autoFocus
                            />

                            <button
                                onClick={handleSubmit}
                                disabled={!formData.department || isLoading}
                                className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-200"
                            >
                                {isLoading ? <Loader className="animate-spin w-6 h-6" /> : <Check className="w-6 h-6" />}
                                Terminer et accéder à ma Veille.
                            </button>
                        </div>
                    )}
                </div>

                {step > 1 && (
                    <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                        <button onClick={() => setStep(step - 1)} className="text-slate-500 hover:text-slate-800 text-sm font-medium transition-colors flex items-center gap-1">
                            <ArrowLeft size={16} /> Étape précédente
                        </button>
                        <span className="text-xs text-slate-400">Étape {step}/5</span>
                    </div>
                )}
            </div>
        </div>
    );
}